import { useEffect, useState } from 'react'
import axios from 'axios';
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, User, Eye, TrendingUp, Award, Briefcase, Sparkles } from 'lucide-react';
import ImageLoader from '../components/ImageLoader';
import Loader from '../components/Loader';

const AllPosts = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [posts, setPosts] = useState([]);
    const [hoveredCard, setHoveredCard] = useState(null);

    useEffect(() => {
        fetchData();
        window.scrollTo(0, 0);
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('https://emr.metadei.in/wp-json/wp/v2/posts?per_page=100');
            const data = Array.isArray(response.data) ? response.data : [];
            const filteredPosts = data.filter(post => post.categories?.includes(1));
            setPosts(filteredPosts);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching case studies:', error);
            setLoading(false);
        }
    };

    const getImageUrl = (imageField) => {
        if (!imageField) return null;
        if (typeof imageField === 'string' && (imageField.startsWith('http') || imageField.startsWith('/'))) {
            return imageField.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
        }
        if (typeof imageField === 'object' && imageField.url) {
            return imageField.url.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
        }
        return null;
    }

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50, filter: 'blur(10px)' },
        visible: {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            transition: { duration: 0.8, type: 'spring', bounce: 0.4 }
        }
    };

    const icons = [TrendingUp, Award, Briefcase, Sparkles, Eye, User];

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <Helmet>
                <title>Case Studies - Connected Bytes Software Service</title>
                <meta property="og:title" content='Case Studies - Connected Bytes Software Service' />
                <meta property="og:description" content='Discover how our software development solutions drive innovation and deliver real results through custom web, mobile, and enterprise applications' />
                <meta property="og:image" content='https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN' />
                <meta property="og:url" content='https://connectedbytes.in/case-studies' />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content='Case Studies - Connected Bytes Software Service' />
                <meta name="twitter:description" content='Discover how our software development solutions drive innovation and deliver real results' />
                <meta name="twitter:image" content='https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN' />
            </Helmet>

            <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
                {/* Hero Section */}
                {/* <div className="relative pt-32 pb-24 overflow-hidden border-b border-border">
                    <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 1px)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 blur-[150px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/4" />
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/10 blur-[150px] rounded-full pointer-events-none translate-y-1/2 -translate-x-1/4" />

                    <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            transition={{ duration: 0.8, type: 'spring' }}
                        >
                            <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-4">Success Stories</span>
                            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black font-heading mb-6 tracking-tight">
                                Case Studies
                                <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mt-2">
                                    Real Results, Real Impact
                                </span>
                            </h1>
                            <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-8 shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-body leading-relaxed">
                                Explore how our elite engineering team has revolutionized enterprises, resolved complex architectural problems, and accelerated growth trajectories.
                            </p>
                        </motion.div>
                    </div>
                </div> */}

                {/* Case Studies Grid */}
                <div className="max-w-7xl mx-auto px-4 py-24 relative z-10">
                    {posts.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground font-body">No case studies available at this time.</p>
                        </div>
                    ) : (
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            {posts.map((post, index) => {
                                const IconComponent = icons[index % icons.length];

                                return (
                                    <motion.div
                                        key={post.id}
                                        variants={cardVariants}
                                        onMouseEnter={() => setHoveredCard(index)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        className="group relative cursor-pointer block h-full"
                                        onClick={() => navigate(`/case-studies/${post.slug}`)}
                                    >
                                        <div className="relative bg-card/60 backdrop-blur-md rounded-3xl overflow-hidden shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-border transition-all duration-500 h-full flex flex-col hover-glow">


                                            {/* Image Container */}
                                            <div className="relative h-64 overflow-hidden bg-gradient-to-br from-muted to-background flex-shrink-0 border-b border-border">
                                                <motion.div
                                                    animate={hoveredCard === index ? { scale: 1.05 } : { scale: 1 }}
                                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                                    className="h-full w-full"
                                                >
                                                    {getImageUrl(post.acf?.seo_image_posts) || getImageUrl(post.acf?.featured_image) ? (
                                                        <img
                                                            src={getImageUrl(post.acf?.seo_image_posts) || getImageUrl(post.acf?.featured_image)}
                                                            alt={post.title?.rendered || 'Case Study'}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                                                            <IconComponent className="w-16 h-16 text-primary drop-shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                                                        </div>
                                                    )}
                                                </motion.div>

                                                {/* Dark Overlay on Hover */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                                {/* Icon Badge */}
                                                <div className="absolute top-4 right-4 w-12 h-12 rounded-2xl bg-card/90 backdrop-blur-md flex items-center justify-center shadow-lg border border-border/50 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 delay-100 z-20">
                                                    <IconComponent className="w-6 h-6 text-primary" />
                                                </div>
                                            </div>

                                            {/* Card Content */}
                                            <div className="p-8 flex flex-col flex-grow relative z-20">
                                                {/* Category Badge */}
                                                <div className="mb-4">
                                                    <span className="inline-block px-4 py-1.5 bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest rounded-full font-secondary">
                                                        Case Study
                                                    </span>
                                                </div>

                                                <div className="mb-6">
                                                    <h3 className="text-2xl font-bold font-heading line-clamp-2 transition-colors duration-300 min-h-[64px] group-hover:text-primary">
                                                        {post.title?.rendered || 'Case Study Title'}
                                                    </h3>
                                                    <div className="w-12 h-1 bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500 group-hover:w-24 opacity-50 group-hover:opacity-100 mt-4" />
                                                </div>

                                                {/* <p className="text-muted-foreground text-sm leading-relaxed font-body mb-6 line-clamp-3 flex-grow">
                                                    {post.acf?.SEO_excerpt_posts || post.excerpt?.rendered?.replace(/<[^>]*>/g, '').substring(0, 120) || 'Discover how our innovative architecture transformed this enterprise infrastructure.'}
                                                </p> */}

                                                {/* Meta Info */}
                                                {/* <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-foreground opacity-60 uppercase tracking-widest font-secondary mb-6 border-b border-border/50 pb-6">
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-primary" />
                                                        <span>{formatDate(post.date)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Eye className="w-4 h-4 text-accent" />
                                                        <span>5 min read</span>
                                                    </div>
                                                </div> */}

                                                {/* Button */}
                                                <motion.button
                                                    whileHover={{ x: 5 }}
                                                    className="mt-auto inline-flex items-center gap-2 text-primary font-bold font-secondary text-sm group/btn uppercase tracking-widest pt-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/case-studies/${post.slug}`);
                                                    }}
                                                >
                                                    <span>Analyze Report</span>
                                                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                                                </motion.button>
                                            </div>

                                        </div>

                                        {/* Glow Effect */}
                                        <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent rounded-[1.8rem] opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 pointer-events-none -z-10" />
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </div>

                {/* CTA Section */}
                <div className="py-24 relative overflow-hidden border-t border-border mt-8">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none" />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[400px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
                    <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
                        <h2 className="text-4xl md:text-5xl font-black font-heading mb-6">
                            Ready to Code Your Success Story?
                        </h2>
                        <p className="text-xl text-muted-foreground mb-10 font-body">
                            Let's discuss how our elite engineering can inject massive growth into your ecosystem.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/contact')}
                            className="px-10 py-4 bg-primary text-primary-foreground rounded-full font-bold shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all duration-300 inline-flex items-center gap-3 font-secondary text-lg"
                        >
                            Start Architecture Planning <ArrowRight className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllPosts;